import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useViewMode } from '@/stores/viewMode'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { logger } from '@magnetar/utils'
import { Settings, Edit, Trash, Copy, Eye } from 'lucide-react'
import { AdScheduleTemplate } from '@/hooks/useAdScheduleTemplates'
import DeleteTemplateDialog from './DeleteTemplateDialog'
import { useAuth } from '@/hooks/useAuth'
import { adScheduleTemplateService } from '@/services/ad_schedule_template_service'
import { toast } from 'sonner'

interface ActionCellProps {
  template: AdScheduleTemplate
  onRefresh?: () => void
}

export default function ActionCell({ template, onRefresh }: ActionCellProps) {
  const navigate = useNavigate()
  const { mode, id: viewModeId } = useViewMode()
  const { token } = useAuth()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // 依當前 scope 組 URL 前綴
  const scopePrefix = mode === 'provider' && viewModeId ? `/provider/${viewModeId}` : '/admin'
  const templateBasePath = `${scopePrefix}/ad-schedule-templates/${template.template_id}`

  const handleDelete = async () => {
    if (!token) return

    setIsDeleting(true)
    try {
      const result = await adScheduleTemplateService.deleteTemplate(template.template_id, token)
      if (result.success) {
        onRefresh?.()
        setDeleteDialogOpen(false)
      }
    } catch (error) {
      logger.error('刪除失敗', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCopy = async () => {
    if (!token) return

    try {
      // 準備複製的梯次資料
      const slotsToCreate = template.slots
        .filter((slot) => slot.is_active)
        .map((slot) => ({
          slot_number: slot.slot_number,
          slot_name: slot.slot_name,
          play_minute: slot.play_minute,
          items: slot.slot_items
            .filter((item) => item.is_active)
            .map((item) => ({
              advertisement_id: item.advertisement_id,
              order: item.order,
              play_duration: item.play_duration,
            })),
        }))

      // 創建新模版，名稱加上 (複製)，包含所有梯次資料
      // 保留原模版的 provider_id，確保複製後的模版在相同的 provider 下可見
      const newTemplate = await adScheduleTemplateService.createTemplate(
        {
          template_name: `${template.template_name} (複製)`,
          description: template.description,
          provider_id: template.provider_id ?? undefined,
          slots: slotsToCreate,
        },
        token,
      )

      if (newTemplate.success && newTemplate.data) {
        toast.success('模版已複製')
        onRefresh?.()
      } else {
        toast.error('複製模版失敗')
      }
    } catch (error) {
      logger.error('複製失敗', error)
      toast.error('複製模版失敗')
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">開啟選單</span>
            <Settings className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>操作</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(templateBasePath)}>
            <Eye className="mr-2 h-4 w-4" />
            查看詳情
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`${templateBasePath}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            編輯模版
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`${templateBasePath}/apply`)}>
            <Settings className="mr-2 h-4 w-4" />
            套用設定
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCopy}>
            <Copy className="mr-2 h-4 w-4" />
            複製模版
          </DropdownMenuItem>
          <DropdownMenuItem className="text-red-600" onClick={() => setDeleteDialogOpen(true)}>
            <Trash className="mr-2 h-4 w-4" />
            刪除模版
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteTemplateDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        template={template}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}
